
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Paintbrush, Upload, Image, ArrowDown, Download, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { clearUserSession } from '@/utils/sessionManager';
import { BANNER_SIZES, BannerSize } from '@/types/banner';
import BannerSpecificationsPanel from '@/components/admin/marketing/BannerSpecificationsPanel';

const MasterBannerCreatorPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSize, setSelectedSize] = useState<BannerSize>('medium_rectangle');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [bannerText, setBannerText] = useState('');
  const [textColor, setTextColor] = useState('#000000');
  const [fontSize, setFontSize] = useState(24);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      clearUserSession();
      toast({ title: "Logged out successfully" });
      navigate('/login');
    } catch (error) {
      toast({ 
        title: "Logout failed", 
        description: "There was a problem logging you out.", 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      toast({
        title: "Image uploaded",
        description: "Your image has been uploaded for the banner.",
      });
    }
  };

  const handlePreviewBanner = () => {
    // This would normally generate a preview using canvas or a library
    // For this demo, we're simulating it with a placeholder
    const sizeConfig = BANNER_SIZES.find(s => s.value === selectedSize);
    setBannerPreview(`https://via.placeholder.com/${sizeConfig?.width}x${sizeConfig?.height}/${backgroundColor.replace('#', '')}/${textColor.replace('#', '')}?text=${encodeURIComponent(bannerText || 'Banner Preview')}`);
    
    toast({
      title: "Preview generated",
      description: "Your banner preview has been generated.",
    });
  };

  const handleSaveBanner = () => {
    toast({
      title: "Banner saved",
      description: "Your banner has been saved to the library.",
    });
    
    // In a real app, you would save this banner to your storage
    navigate('/master/marketing/manage-banners');
  };

  return (
    <MasterLicenseLayout 
      isLoading={isLoading} 
      onLogout={handleLogout}
      activeTab="banner-creator"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Banner Creator</h1>
            <p className="text-gray-600">Design custom marketing banners for your campaigns</p>
          </div>
          <div className="mt-4 md:mt-0 space-x-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/master/marketing/ai-banner')}
            >
              Switch to AI Generator
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/master/marketing/manage-banners')}
            >
              View Banner Library
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Banner Specifications</CardTitle>
                <CardDescription>Select the size and format for your banner</CardDescription>
              </CardHeader>
              <CardContent>
                <BannerSpecificationsPanel 
                  selectedSize={selectedSize} 
                  onSizeChange={setSelectedSize} 
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Banner Design</CardTitle>
                <CardDescription>Customize the appearance of your banner</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="basics">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basics">Basics</TabsTrigger>
                    <TabsTrigger value="text">Text</TabsTrigger>
                    <TabsTrigger value="image">Image</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="basics" className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="background-color">Background Color</Label>
                      <div className="flex items-center space-x-2 mt-1">
                        <div 
                          className="h-8 w-8 rounded border" 
                          style={{ backgroundColor: backgroundColor }}
                        ></div>
                        <Input
                          id="background-color"
                          type="color"
                          value={backgroundColor}
                          onChange={(e) => setBackgroundColor(e.target.value)}
                          className="w-12 h-10 p-1"
                        />
                        <Input
                          type="text"
                          value={backgroundColor}
                          onChange={(e) => setBackgroundColor(e.target.value)}
                          className="w-32"
                        />
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="text" className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="banner-text">Banner Text</Label>
                      <Input
                        id="banner-text"
                        value={bannerText}
                        onChange={(e) => setBannerText(e.target.value)}
                        className="mt-1"
                        placeholder="Enter text for your banner"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="text-color">Text Color</Label>
                      <div className="flex items-center space-x-2 mt-1">
                        <div 
                          className="h-8 w-8 rounded border" 
                          style={{ backgroundColor: textColor }}
                        ></div>
                        <Input
                          id="text-color"
                          type="color"
                          value={textColor}
                          onChange={(e) => setTextColor(e.target.value)}
                          className="w-12 h-10 p-1"
                        />
                        <Input
                          type="text"
                          value={textColor}
                          onChange={(e) => setTextColor(e.target.value)}
                          className="w-32"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="font-size">Font Size: {fontSize}px</Label>
                      </div>
                      <Slider
                        id="font-size"
                        min={10}
                        max={72}
                        step={1}
                        value={[fontSize]}
                        onValueChange={(value) => setFontSize(value[0])}
                        className="mt-2"
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="image" className="space-y-4 pt-4">
                    <div>
                      <Label>Background Image</Label>
                      <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center mt-1">
                        <input
                          type="file"
                          id="image-upload"
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileUpload}
                        />
                        <label htmlFor="image-upload" className="cursor-pointer">
                          <div className="flex flex-col items-center justify-center py-4">
                            <Upload className="h-10 w-10 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600 font-medium">
                              Click to upload an image
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              PNG, JPG up to 5MB
                            </p>
                          </div>
                        </label>
                      </div>
                      
                      {imagePreview && (
                        <div className="mt-4">
                          <p className="text-sm font-medium mb-2">Uploaded Image:</p>
                          <div className="relative h-32 bg-gray-100 rounded-md overflow-hidden">
                            <img 
                              src={imagePreview} 
                              alt="Uploaded" 
                              className="h-full w-auto object-contain mx-auto"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            
            <div className="flex justify-end space-x-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/master/marketing/manage-banners')}
              >
                Cancel
              </Button>
              <Button 
                variant="secondary" 
                onClick={handlePreviewBanner}
              >
                <ArrowDown className="h-4 w-4 mr-1" /> Preview
              </Button>
              <Button onClick={handleSaveBanner} disabled={!bannerPreview}>
                <Check className="h-4 w-4 mr-1" /> Save Banner
              </Button>
            </div>
          </div>
          
          <div className="lg:col-span-1">
            <Card className="h-full flex flex-col">
              <CardHeader>
                <CardTitle>Banner Preview</CardTitle>
                <CardDescription>See how your banner will appear</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow flex flex-col items-center justify-center">
                {bannerPreview ? (
                  <div className="border rounded p-2 bg-white">
                    <img
                      src={bannerPreview}
                      alt="Banner Preview"
                      className="max-w-full h-auto"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-gray-300 rounded-lg">
                    <Image className="h-12 w-12 text-gray-400 mb-3" />
                    <p className="text-lg font-medium text-gray-600">No preview yet</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Click the Preview button to see your banner
                    </p>
                  </div>
                )}
                
                {bannerPreview && (
                  <Button 
                    variant="outline" 
                    className="mt-4 w-full flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" /> 
                    Download Preview
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MasterLicenseLayout>
  );
};

export default MasterBannerCreatorPage;
